import {
  LayoutDashboard,
  UtensilsCrossed,
  PackageOpen,
  FileText,
  BookOpen,
  Users,
  LogOut,
  Settings,
  ToggleRight,
  UserCircle,
  ScrollText,
  Moon,
  Sun,
  ChevronUp,
  Contact,
  CalendarDays,
  Sparkles,
  Globe,
  Quote,
  Award,
  Megaphone,
  BarChart3,
  LineChart,
  Palette,
  Bot,
  Film,
  // Aliased: a bare `Map` would shadow the global Map constructor in this file.
  Map as MapIcon,
  CalendarCheck,
  Share2,
  Activity,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTheme } from "next-themes";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

interface NavItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  moduleKey: string;
  adminOnly?: boolean;
  comingSoon?: boolean;
}

const overviewItems: NavItem[] = [
  { title: "Dashboard", url: "/admin", icon: LayoutDashboard, moduleKey: "dashboard" },
];

const menuItems: NavItem[] = [
  { title: "Menu Items", url: "/admin/menu-items", icon: UtensilsCrossed, moduleKey: "menu_items" },
  { title: "Catalog", url: "/admin/catalog", icon: BookOpen, moduleKey: "packages" },
];

const businessItems: NavItem[] = [
  { title: "Quotes", url: "/admin/quotes", icon: FileText, moduleKey: "quotes" },
  // Events is the post-booking ops view (#1032): Quotes is where sales happens,
  // Events is where booked work gets run, over the same events rows. It is always
  // on for staff+ and has no feature_flags row on purpose. filterItems gates it on
  // role, like roadmap, to mirror /admin/events's requiredRole="staff" route.
  { title: "Events", url: "/admin/events", icon: CalendarCheck, moduleKey: "events" },
  { title: "Automations", url: "/admin/automations", icon: Bot, moduleKey: "automations" },
  { title: "Clients", url: "/admin/clients", icon: Contact, moduleKey: "clients" },
  { title: "Calendar", url: "/admin/calendar", icon: CalendarDays, moduleKey: "calendar", comingSoon: true },
];

const toolsItems: NavItem[] = [
  { title: "AI Chef", url: "/admin/ai-chef", icon: Sparkles, moduleKey: "ai_chef", comingSoon: true },
  { title: "Meta Ads", url: "/admin/ads", icon: BarChart3, moduleKey: "meta_ads", adminOnly: true },
  // No adminOnly: the route is requiredRole="staff" (#747). Read-only, flag-gated.
  { title: "Live Ads", url: "/admin/live-ads", icon: Activity, moduleKey: "meta_ads_overview" },
  // No adminOnly: the route is requiredRole="staff" (#519).
  { title: "Google Ads", url: "/admin/google-ads", icon: LineChart, moduleKey: "google_ads" },
  // No adminOnly: the route is requiredRole="staff" (#975). Read-only, flag-gated.
  { title: "Social", url: "/admin/social", icon: Share2, moduleKey: "social" },
  // No adminOnly: the flag ships off, and filterItems short-circuits on
  // isSuperAdmin, so this is super_admin-only in practice (#498).
  { title: "Studio", url: "/admin/studio", icon: Film, moduleKey: "studio" },
];

const websiteItems: NavItem[] = [
  { title: "Branding", url: "/admin/branding", icon: Palette, moduleKey: "branding" },
  { title: "Testimonials", url: "/admin/website/testimonials", icon: Quote, moduleKey: "website_testimonials" },
  { title: "Why Choose Us", url: "/admin/website/why-choose-us", icon: Award, moduleKey: "website_why_choose_us" },
  { title: "Content Settings", url: "/admin/website/content", icon: Megaphone, moduleKey: "website_content_settings" },
  { title: "Marketing Pages", url: "/admin/website/pages", icon: Megaphone, moduleKey: "marketing_pages" },
  { title: "Services", url: "/admin/website/services", icon: Sparkles, moduleKey: "services" },
];

const systemItems: NavItem[] = [
  { title: "Audit Log", url: "/admin/changelog", icon: ScrollText, moduleKey: "changelog" },
  // No feature_flags row for "roadmap" on purpose — filterItems gates it on role
  // instead (see below). A row seeded false would hide it from the staff it is for,
  // and would make showSoonBadge stamp a "Soon" chip on an always-available page.
  { title: "Roadmap", url: "/admin/roadmap", icon: MapIcon, moduleKey: "roadmap" },
  { title: "Staff", url: "/admin/staff", icon: Users, moduleKey: "staff" },
  { title: "Team", url: "/admin/team", icon: Users, moduleKey: "team", adminOnly: true },
  { title: "Settings", url: "/admin/settings", icon: Settings, moduleKey: "settings", adminOnly: true },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { profile, roles, hasRole, isSuperAdmin, featureFlags, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const isAdmin = hasRole("admin");

  const { data: pendingFeedbackCount } = useQuery({
    queryKey: ["pending-feedback-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("user_feedback")
        .select("*", { count: "exact", head: true })
        .neq("status", "done");
      if (error) throw error;
      return count ?? 0;
    },
    enabled: isAdmin,
    refetchInterval: 60_000,
  });

  const filterItems = (items: NavItem[]) =>
    items.filter((item) => {
      if (isSuperAdmin) return true;
      if (item.moduleKey === "changelog") return true;
      // Coming soon items are always visible to staff+
      if (item.comingSoon) return hasRole("staff");
      // Roadmap is gated on role, not on a flag, and this line MUST stay above the
      // featureFlags check below: there is deliberately no "roadmap" flag row, so
      // featureFlags["roadmap"] is undefined and the next line would hide the entry
      // from every staff member and admin with nothing going red. The predicate
      // mirrors /admin/roadmap's own gate (ProtectedRoute requiredRole="staff", whose
      // adminCarveOut admits plain admins) — hasRole is exact-match, so hasRole("staff")
      // alone would be false for an admin who can open the page (#564). Events is
      // gated the same way for the same reason (#1032): no "events" flag row.
      if (item.moduleKey === "roadmap" || item.moduleKey === "events") {
        return hasRole("staff") || hasRole("admin");
      }
      if (!featureFlags[item.moduleKey]) return false;
      if (item.adminOnly && !hasRole("admin")) return false;
      return true;
    });

  const renderGroup = (label: string, items: NavItem[]) => {
    const visible = filterItems(items);
    if (visible.length === 0) return null;
    return (
      <SidebarGroup>
        <SidebarGroupLabel className="text-[10px] uppercase tracking-widest font-semibold text-sidebar-foreground/40 px-3">
          {label}
        </SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            {visible.map((item) => {
              const badgeCount =
                item.moduleKey === "changelog" && isAdmin ? (pendingFeedbackCount ?? 0) : 0;
              // "Soon" marks any module whose feature flag is explicitly off
              // (i.e. currently gated), regardless of the hardcoded comingSoon
              // hint — comingSoon now only governs staff visibility below.
              const showSoonBadge = featureFlags[item.moduleKey] === false;
              return (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      end={item.url === "/admin"}
                      className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                      activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      {!collapsed && (
                        <>
                          <span className={showSoonBadge ? "text-sidebar-foreground/50" : ""}>{item.title}</span>
                          {showSoonBadge && (
                            <span className="ml-auto text-[9px] font-medium uppercase tracking-wider text-sidebar-foreground/30 bg-sidebar-accent rounded px-1.5 py-0.5">
                              Soon
                            </span>
                          )}
                        </>
                      )}
                      {badgeCount > 0 && !collapsed && (
                        <span className="ml-auto flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1">
                          {badgeCount}
                        </span>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  };

  const initials = profile?.display_name
    ? profile.display_name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "?";

  const primaryRole = roles[0] ?? "user";

  return (
    <Sidebar collapsible="icon">
      {/* ── Header ── */}
      <SidebarHeader className="border-b border-sidebar-border px-3 py-4 overflow-hidden">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground font-logo font-bold text-sm">
            T
          </div>
          {!collapsed && (
            <div className="overflow-hidden">
              <h1 className="font-logo text-base font-bold leading-tight text-sidebar-foreground truncate">
                Tristano's
              </h1>
              <p className="text-[10px] text-sidebar-foreground/50 truncate">
                Catering Management
              </p>
            </div>
          )}
        </div>
      </SidebarHeader>

      {/* ── Navigation ── */}
      <SidebarContent className="py-2">
        {renderGroup("Overview", overviewItems)}
        {renderGroup("Menu", menuItems)}
        {renderGroup("Business", businessItems)}
        {renderGroup("Tools", toolsItems)}
        {renderGroup("Website", websiteItems)}
        {renderGroup("System", systemItems)}

        {isSuperAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-[10px] uppercase tracking-widest font-semibold text-sidebar-foreground/40 px-3">
              Super Admin
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to="/admin/feature-flags"
                      className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                      activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                    >
                      <ToggleRight className="h-4 w-4 shrink-0" />
                      {!collapsed && <span>Feature Flags</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      {/* ── Footer — compact user menu ── */}
      <SidebarFooter className="border-t border-sidebar-border p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton className="w-full hover:bg-sidebar-accent rounded-md px-2 py-2 h-auto">
                  <div className="flex items-center gap-2.5 w-full">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sidebar-accent text-sidebar-accent-foreground text-xs font-semibold">
                      {initials}
                    </div>
                    {!collapsed && (
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-medium text-sidebar-foreground truncate leading-tight">
                          {profile?.display_name ?? "User"}
                        </p>
                        <p className="text-[10px] text-sidebar-foreground/50 capitalize leading-tight">
                          {primaryRole}
                        </p>
                      </div>
                    )}
                    {!collapsed && (
                      <ChevronUp className="h-4 w-4 text-sidebar-foreground/40 shrink-0" />
                    )}
                  </div>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="top"
                align="start"
                className="w-56"
              >
                <div className="px-3 py-2">
                  <p className="text-sm font-medium truncate">{profile?.display_name}</p>
                  <div className="flex gap-1 mt-1">
                    {roles.map((role) => (
                      <Badge key={role} variant="secondary" className="text-[10px] px-1.5 py-0 h-4 capitalize">
                        {role}
                      </Badge>
                    ))}
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <NavLink to="/admin/account" className="flex items-center gap-2 cursor-pointer">
                    <UserCircle className="h-4 w-4" />
                    My Account
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  {theme === "dark" ? "Light Mode" : "Dark Mode"}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={signOut}
                  className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
